import { useState, useCallback, useRef, useEffect } from 'react';
import { Notification } from '../components/NotificationSystem';

interface NotificationOptions {
  debounceMs?: number;
  throttleMs?: number;
  maxNotifications?: number;
  priority?: 'low' | 'medium' | 'high';
  deduplicate?: boolean;
}

const DEFAULT_OPTIONS: NotificationOptions = {
  debounceMs: 2000, // Wait 2 seconds before showing similar notifications
  throttleMs: 5000, // Minimum 5 seconds between notifications
  maxNotifications: 3, // Maximum 3 notifications visible at once
  deduplicate: true, // Prevent duplicate messages
};

// Priority levels for different notification types
const PRIORITY_LEVELS = {
  error: 3,
  warning: 2,
  success: 1,
  info: 0,
};

export const useNotifications = (options: NotificationOptions = {}) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const notificationHistory = useRef<Map<string, number>>(new Map());
  const lastNotificationTime = useRef<number>(0);
  const pendingNotifications = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const suppressedCount = useRef<number>(0);
  
  const config = { ...DEFAULT_OPTIONS, ...options };

  // Clean up old history entries (older than 1 minute)
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const now = Date.now();
      const oneMinuteAgo = now - 60000;
      
      notificationHistory.current.forEach((timestamp, key) => {
        if (timestamp < oneMinuteAgo) {
          notificationHistory.current.delete(key);
        }
      });
    }, 30000); // Clean every 30 seconds

    return () => clearInterval(cleanupInterval);
  }, []);

  const shouldShowNotification = useCallback((
    message: string,
    type: Notification['type']
  ): boolean => {
    const now = Date.now();
    
    // Check throttling
    if (now - lastNotificationTime.current < (config.throttleMs || 0)) {
      suppressedCount.current++;
      return false;
    }
    
    // Check if we've shown this message recently (deduplication)
    if (config.deduplicate) {
      const messageKey = `${type}:${message}`;
      const lastShown = notificationHistory.current.get(messageKey);
      
      if (lastShown && now - lastShown < 30000) { // 30 seconds cooldown for same message
        suppressedCount.current++;
        return false;
      }
    }
    
    // Check max notifications limit
    if (notifications.length >= (config.maxNotifications || Infinity)) {
      // Only show if it's higher priority than existing notifications
      const currentMinPriority = Math.min(
        ...notifications.map(n => PRIORITY_LEVELS[n.type])
      );
      
      if (PRIORITY_LEVELS[type] <= currentMinPriority) {
        suppressedCount.current++;
        return false;
      }
    }
    
    return true;
  }, [notifications, config]);

  const addNotification = useCallback((
    notification: Omit<Notification, 'id'>
  ) => {
    const { message, type = 'info' } = notification;
    
    // Check if we should show this notification
    if (!shouldShowNotification(message, type)) {
      // If we've suppressed too many, show a summary notification
      if (suppressedCount.current >= 5) {
        const summaryId = `summary-${Date.now()}`;
        setNotifications(prev => [...prev, {
          id: summaryId,
          type: 'info',
          title: 'Multiple Updates',
          message: `${suppressedCount.current} notifications were suppressed to reduce noise`,
          duration: 3000,
        }]);
        suppressedCount.current = 0;
      }
      return;
    }
    
    const id = `${type}-${Date.now()}-${Math.random()}`;
    const messageKey = `${type}:${message}`;
    
    // Cancel any pending notification with the same message
    if (pendingNotifications.current.has(messageKey)) {
      clearTimeout(pendingNotifications.current.get(messageKey)!);
    }
    
    // Debounce the notification
    const timeout = setTimeout(() => {
      const now = Date.now();
      
      // Final check before showing
      if (now - lastNotificationTime.current >= (config.throttleMs || 0)) {
        setNotifications(prev => {
          // Remove oldest if at max capacity
          if (prev.length >= (config.maxNotifications || 3)) {
            return [...prev.slice(1), { ...notification, id }];
          }
          return [...prev, { ...notification, id }];
        });
        
        lastNotificationTime.current = now;
        notificationHistory.current.set(messageKey, now);
      }
      
      pendingNotifications.current.delete(messageKey);
    }, config.debounceMs || 0);
    
    pendingNotifications.current.set(messageKey, timeout);
  }, [shouldShowNotification, config]);

  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
    pendingNotifications.current.forEach(timeout => clearTimeout(timeout));
    pendingNotifications.current.clear();
  }, []);

  // Smart notification filtering based on context
  const smartAddNotification = useCallback((
    notification: Omit<Notification, 'id'>
  ) => {
    const { type, message } = notification;
    
    // Filter out low-priority success messages
    if (type === 'success' && !message.includes('important')) {
      // Only show 1 in 3 success messages
      if (Math.random() > 0.33) {
        return;
      }
    }
    
    // Filter out repetitive info messages
    if (type === 'info') {
      // Only show 1 in 5 info messages
      if (Math.random() > 0.2) {
        return;
      }
    }
    
    // Always show errors and warnings
    if (type === 'error' || type === 'warning') {
      // But still apply deduplication
      addNotification({
        ...notification,
        duration: notification.duration || 5000, // Longer duration for important messages
      });
      return;
    }
    
    // For other messages, use standard logic
    addNotification({
      ...notification,
      duration: notification.duration || 3000, // Shorter duration for regular messages
    });
  }, [addNotification]);

  return {
    notifications,
    addNotification: smartAddNotification,
    removeNotification,
    clearAll,
    suppressedCount: suppressedCount.current,
  };
};

// Global notification throttler
class NotificationThrottler {
  private static instance: NotificationThrottler;
  private lastNotificationByType: Map<string, number> = new Map();
  private globalLastNotification: number = 0;
  
  private constructor() {}
  
  static getInstance(): NotificationThrottler {
    if (!NotificationThrottler.instance) {
      NotificationThrottler.instance = new NotificationThrottler();
    }
    return NotificationThrottler.instance;
  }
  
  shouldAllow(type: string, minInterval: number = 3000): boolean {
    const now = Date.now();
    
    // Global throttle - no more than 1 notification per second
    if (now - this.globalLastNotification < 1000) {
      return false;
    }
    
    // Type-specific throttle
    const lastOfType = this.lastNotificationByType.get(type) || 0;
    if (now - lastOfType < minInterval) {
      return false;
    }
    
    this.globalLastNotification = now;
    this.lastNotificationByType.set(type, now);
    return true;
  }
  
  reset() {
    this.lastNotificationByType.clear();
    this.globalLastNotification = 0;
  }
}

export const notificationThrottler = NotificationThrottler.getInstance();